import { useMutation, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { avisar } from '@xiltrion/ui/sonner';
import type { Persona } from '@xiltrion/ui/bloques/personas';
import type { TaskCreateInput, TaskMoveInput, TaskQuery, TaskUpdateInput } from '@planning/contracts';
import { apiDelete, apiGet, apiPatch, apiPost, ApiError } from './client';
import type { Label, PersonRow, ProjectDetail, ProjectRow, TaskDetail, TaskItem, TeamRow, Workspace } from './types';

export const keys = {
  people: ['people'] as const,
  projects: (archived = false) => ['projects', { archived }] as const,
  project: (id: string) => ['project', id] as const,
  tasks: (q: Partial<TaskQuery>) => ['tasks', q] as const,
  task: (id: string) => ['task', id] as const,
  labels: ['labels'] as const,
  teams: ['teams'] as const,
  workspaces: ['workspaces'] as const,
};

export const errorMessage = (e: unknown) => (e instanceof ApiError ? e.message : 'Algo salió mal. Intenta de nuevo.');
export const avisarError = (e: unknown) => avisar.error(errorMessage(e));

export function usePeople() {
  return useQuery({ queryKey: keys.people, queryFn: () => apiGet<PersonRow[]>('/people'), staleTime: 5 * 60_000 });
}

/** Personas en el formato de los bloques de Xiltrion UI. */
export function usePersonas(): { personas: Persona[]; porId: Map<string, Persona> } {
  const { data = [] } = usePeople();
  const personas = data.map((p) => ({ id: p.id, nombre: p.displayName, correo: p.email, color: p.avatarColor, externo: p.external, detalle: p.jobTitle ?? p.email }));
  return { personas, porId: new Map(personas.map((p) => [p.id, p])) };
}

/**
 * Equipos con su nombre completo («Secretaría de Salud › Vacunación») y los que tocan a una persona: los que integra o
 * dirige y los subequipos de los que dirige (la misma regla que `teamScope` en el API). `options` son los que pueden
 * recibir tareas (un archivado ya no; `optionsWith` agrega el que ya tenía); `allOptions`, todos, para filtrar.
 */
export function useTeams(userId?: string | null) {
  const { data = [] } = useQuery({ queryKey: keys.teams, queryFn: () => apiGet<TeamRow[]>('/teams'), staleTime: 5 * 60_000 });
  const byId = new Map(data.map((t) => [t.id, t]));
  const fullName = (t: TeamRow) => t.path.split('/').map((id) => byId.get(id)?.name ?? '').filter(Boolean).join(' › ');
  const managed = data.filter((t) => t.managerId === userId);
  const mine = new Set(userId ? data.filter((t) => t.memberIds.includes(userId) || t.managerId === userId || managed.some((m) => t.path.startsWith(`${m.path}/`))).map((t) => t.id) : []);
  const option = (t: TeamRow) => ({ value: t.id, label: t.active ? fullName(t) : `${fullName(t)} (archivado)` });
  const options = data.filter((t) => t.active).map(option);
  const optionsWith = (id: string | null | undefined) => { const t = id ? byId.get(id) : undefined; return t && !t.active ? [...options, option(t)] : options; };
  return { teams: data, byId, fullName, mine, options, optionsWith, allOptions: data.map(option) };
}

export function useProjects(archived = false) {
  return useQuery({ queryKey: keys.projects(archived), queryFn: () => apiGet<ProjectRow[]>('/projects', { archived }) });
}

export function useProject(id: string | undefined) {
  return useQuery({ queryKey: keys.project(id ?? ''), queryFn: () => apiGet<ProjectDetail>(`/projects/${id}`), enabled: !!id });
}

export function useWorkspaces() {
  return useQuery({ queryKey: keys.workspaces, queryFn: () => apiGet<Workspace[]>('/workspaces') });
}

export function useLabels() {
  return useQuery({ queryKey: keys.labels, queryFn: () => apiGet<Label[]>('/labels') });
}

export function useTasks(q: Partial<TaskQuery>, enabled = true) {
  return useQuery({ queryKey: keys.tasks(q), queryFn: () => apiGet<{ items: TaskItem[]; truncated: boolean }>('/tasks', q as Record<string, string>), enabled });
}

export function useTask(id: string | null) {
  return useQuery({ queryKey: keys.task(id ?? ''), queryFn: () => apiGet<TaskDetail>(`/tasks/${id}`), enabled: !!id });
}

/** Aplica un cambio a toda lista de tareas en caché (optimista) y devuelve cómo deshacerlo. */
function patchTaskLists(qc: ReturnType<typeof useQueryClient>, taskId: string, patch: (t: TaskItem) => TaskItem | null) {
  const snapshots: Array<[QueryKey, unknown]> = [];
  for (const [key, data] of qc.getQueriesData<{ items: TaskItem[] }>({ queryKey: ['tasks'] })) {
    if (!data) continue;
    snapshots.push([key, data]);
    qc.setQueryData(key, { ...data, items: data.items.flatMap((t) => (t.id === taskId ? ((x) => (x ? [x] : []))(patch(t)) : [t])) });
  }
  return () => snapshots.forEach(([k, d]) => qc.setQueryData(k, d));
}

export function useInvalidateWork() {
  const qc = useQueryClient();
  return (taskId?: string, projectId?: string) => {
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    if (taskId) void qc.invalidateQueries({ queryKey: keys.task(taskId) });
    if (projectId) void qc.invalidateQueries({ queryKey: keys.project(projectId) });
    void qc.invalidateQueries({ queryKey: ['projects'] });
    void qc.invalidateQueries({ queryKey: ['home'] });
  };
}

export function useUpdateTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: TaskUpdateInput }) => apiPatch<TaskItem>(`/tasks/${id}`, patch),
    onMutate: ({ id, patch }) => {
      const undo = patchTaskLists(qc, id, (t) => ({
        ...t,
        ...(patch as Partial<TaskItem>),
        ...(patch.completed !== undefined ? { completedAt: patch.completed ? new Date().toISOString() : null } : {}),
      }));
      return { undo };
    },
    onError: (e, _v, ctx) => {
      ctx?.undo();
      avisarError(e);
    },
    onSettled: (_d, _e, v) => invalidate(v.id),
  });
}

export function useMoveTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: ({ id, move }: { id: string; move: TaskMoveInput; optimistic?: Partial<TaskItem> }) => apiPost<TaskItem>(`/tasks/${id}/move`, move),
    onMutate: ({ id, optimistic }) => ({ undo: optimistic ? patchTaskLists(qc, id, (t) => ({ ...t, ...optimistic })) : () => undefined }),
    onError: (e, _v, ctx) => {
      ctx?.undo();
      avisarError(e);
    },
    onSettled: (_d, _e, v) => invalidate(v.id),
  });
}

export function useCreateTask() {
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: (input: TaskCreateInput) => apiPost<TaskItem>('/tasks', input),
    onError: avisarError,
    onSuccess: (t) => invalidate(t.id, t.projectId),
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  const invalidate = useInvalidateWork();
  return useMutation({
    mutationFn: (id: string) => apiDelete(`/tasks/${id}`),
    onMutate: (id) => ({ undo: patchTaskLists(qc, id, () => null) }),
    onError: (e, _v, ctx) => {
      ctx?.undo();
      avisarError(e);
    },
    onSuccess: (_d, id) => {
      invalidate();
      avisar.success('Tarea borrada', { action: { label: 'Deshacer', onClick: () => void apiPost(`/tasks/${id}/restore`).then(() => invalidate()) } });
    },
  });
}
